/**
 * Player identity, verified by email.
 *
 * A player proves they control an address by entering a code sent to it. The
 * server then trusts its own session token rather than anything the client
 * claims — which is what makes "download my data" safe to offer, and stops a
 * score being submitted under someone else's address.
 *
 * The token lives in localStorage. That is a deliberate trade: a cookie would
 * bring CSRF exposure and this app has a strict CSP with no inline scripts, so
 * the XSS surface localStorage exposes is small. It is still a bearer token —
 * see PRIVACY.md on why TLS is a prerequisite for using this for real.
 */

/**
 * Version of the privacy notice a player was shown.
 *
 * Bump this whenever the notice changes materially. Consent is only evidence of
 * anything if you can show WHAT was agreed to, so the version is stored with
 * every record.
 */
export const PRIVACY_NOTICE_VERSION = '2026-08-28';

export interface Player {
  email: string;
  name: string;
  /**
   * Opt-in to being contacted about ReleaseTEAM services.
   *
   * Separate from playing and defaulting to false: consent has to be freely
   * given, specific and unbundled, and a pre-ticked box is not consent at all.
   */
  marketingConsent: boolean;
  /** Which version of the notice was on screen when they agreed. */
  noticeVersion: string;
  /** When this address was first proven. */
  verifiedAt: string;
  lastSeenAt: string;
}

const PLAYER_KEY = 'devtool-detective.player';
const TOKEN_KEY = 'devtool-detective.session';

// ------------------------------------------------------------------ storage

function readLocal(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private browsing or disabled storage.
    return null;
  }
}

function writeLocal(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* best effort */
  }
}

function removeLocal(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* best effort */
  }
}

export function loadPlayer(): Player | null {
  const raw = readLocal(PLAYER_KEY);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Player).email === 'string'
    ) {
      return parsed as Player;
    }
  } catch {
    /* corrupt value */
  }
  return null;
}

export function savePlayer(player: Player): void {
  writeLocal(PLAYER_KEY, JSON.stringify(player));
}

export function loadToken(): string | null {
  return readLocal(TOKEN_KEY);
}

export function clearPlayer(): void {
  removeLocal(PLAYER_KEY);
  removeLocal(TOKEN_KEY);
}

// --------------------------------------------------------------- validation

/**
 * Deliberately permissive email check.
 *
 * Strict RFC-shaped validation rejects real addresses. Its only job here is to
 * catch typos before we spend a send on them — the code sent to the address is
 * what actually proves it exists.
 */
export function isPlausibleEmail(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed.length < 6 || trimmed.length > 254) return false;
  if (/\s/.test(trimmed)) return false;
  const at = trimmed.indexOf('@');
  if (at < 1 || at !== trimmed.lastIndexOf('@')) return false;
  const domain = trimmed.slice(at + 1);
  return domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.');
}

export function isPlausibleName(value: string): boolean {
  return value.trim().length >= 2;
}

export function isPlausibleCode(value: string): boolean {
  return /^\d{6}$/.test(value.trim());
}

// ---------------------------------------------------------------- transport

interface ApiResult {
  ok: boolean;
  status: number;
  body: Record<string, unknown> | null;
}

/** Never throws: a network failure is a result, not an exception, to the UI. */
async function call(
  path: string,
  init: RequestInit = {},
): Promise<ApiResult> {
  const token = loadToken();
  try {
    const response = await fetch(path, {
      ...init,
      headers: {
        ...(init.body ? { 'Content-Type': 'application/json' } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(init.headers ?? {}),
      },
    });
    let body: Record<string, unknown> | null = null;
    try {
      body = (await response.json()) as Record<string, unknown>;
    } catch {
      /* not all responses carry a body */
    }
    return { ok: response.ok, status: response.status, body };
  } catch {
    return { ok: false, status: 0, body: null };
  }
}

const post = (path: string, body: unknown) =>
  call(path, { method: 'POST', body: JSON.stringify(body) });

// --------------------------------------------------------------------- auth

export interface RequestCodeInput {
  email: string;
  name: string;
  marketingConsent: boolean;
}

export type RequestCodeOutcome =
  | { status: 'sent'; expiresInMinutes: number }
  | { status: 'rate-limited' }
  /** The server accepted the request but could not deliver the mail. */
  | { status: 'send-failed' }
  | { status: 'error' };

export async function requestCode(input: RequestCodeInput): Promise<RequestCodeOutcome> {
  const result = await post('/api/auth/request', {
    email: input.email.trim().toLowerCase(),
    name: input.name.trim(),
    marketingConsent: input.marketingConsent,
    noticeVersion: PRIVACY_NOTICE_VERSION,
  });

  if (result.ok) {
    const minutes = Number(result.body?.expiresInMinutes) || 10;
    return { status: 'sent', expiresInMinutes: minutes };
  }
  if (result.status === 429) return { status: 'rate-limited' };
  if (result.body?.error === 'send-failed') return { status: 'send-failed' };
  return { status: 'error' };
}

export type VerifyOutcome =
  | { status: 'verified'; player: Player }
  | { status: 'wrong-code'; remaining?: number }
  | { status: 'expired' }
  | { status: 'locked' }
  | { status: 'error' };

export async function verifyCode(email: string, code: string): Promise<VerifyOutcome> {
  const result = await post('/api/auth/verify', {
    email: email.trim().toLowerCase(),
    code: code.trim(),
  });

  if (result.ok && typeof result.body?.token === 'string') {
    const player = result.body.player as Player;
    writeLocal(TOKEN_KEY, result.body.token);
    savePlayer(player);
    return { status: 'verified', player };
  }

  const reason = String(result.body?.error ?? '');
  if (reason === 'too-many-attempts') return { status: 'locked' };
  if (reason === 'expired' || reason === 'no-challenge') return { status: 'expired' };
  if (reason === 'wrong-code') {
    const remaining = Number(result.body?.remaining);
    return Number.isFinite(remaining)
      ? { status: 'wrong-code', remaining }
      : { status: 'wrong-code' };
  }
  return { status: 'error' };
}

/**
 * Confirm a stored session is still good.
 *
 * Returns the server's view of the account, so a token that has expired or was
 * invalidated by erasure signs the player out rather than leaving a stale
 * identity on screen.
 */
export async function resumeSession(): Promise<Player | null> {
  if (!loadToken()) return null;
  const result = await call('/api/me');
  if (result.ok && result.body?.player) {
    const player = result.body.player as Player;
    savePlayer(player);
    return player;
  }
  if (result.status === 401) clearPlayer();
  // A network failure is not proof the session is bad, so keep what we have.
  return result.status === 0 ? loadPlayer() : null;
}

// ------------------------------------------------------------------- rights

/** Erase everything held about the signed-in account, server included. */
export async function eraseMyData(): Promise<boolean> {
  const result = await post('/api/erase', {});
  clearPlayer();
  return result.ok;
}

/**
 * Download everything held about the signed-in account.
 *
 * Only offered because identity is verified. Without that this endpoint would
 * hand personal data to anyone able to guess an address.
 */
export function myDataDownloadUrl(): string {
  return '/api/me/data';
}

export async function downloadMyData(): Promise<boolean> {
  const result = await call(myDataDownloadUrl());
  if (!result.ok) return false;
  return true;
}

// ------------------------------------------------------------------ results

export interface ResultSubmission {
  scenarioId: string;
  scenarioTitle: string;
  score: number;
  rating: string;
  found: number;
  total: number;
  missed: number;
  falsePositives: number;
}

/**
 * Submit a result and get back a public share link.
 *
 * The player's identity is no longer sent: the server takes it from the
 * session, so a client cannot attribute a score to someone else.
 */
export async function submitResult(
  submission: ResultSubmission,
): Promise<{ shareUrl: string } | null> {
  const result = await post('/api/results', submission);
  const shareUrl = result.body?.shareUrl;
  return result.ok && typeof shareUrl === 'string' ? { shareUrl } : null;
}

/** Official LinkedIn share endpoint. It accepts a URL and nothing else. */
export function linkedInShareUrl(shareUrl: string): string {
  return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
}
